import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { LeftOutlined, RightOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { fetchProductByShowroomAndRecord } from "../../Redux/slice/productSlice";
import { fetchHomePageShowrooms } from "../../Redux/slice/showRoomSlice";

const ComboComponent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { homePageShowrooms } = useSelector((state) => state.showrooms);
  const { productsByShowroom, loading } = useSelector((state) => state.products);

  const [imageLoading, setImageLoading] = useState({});
  const [showArrows, setShowArrows] = useState({});
  const containerRefs = useRef({});

  useEffect(() => {
    dispatch(fetchHomePageShowrooms());
  }, [dispatch]);

  useEffect(() => {
    if (homePageShowrooms?.length > 0) {
      homePageShowrooms.forEach((showroom) => {
        dispatch(fetchProductByShowroomAndRecord({ showRoomCode: showroom.showRoomID, recordNumber: 10 }));
      });
    }
  }, [dispatch, homePageShowrooms]);

  useEffect(() => {
    const updateArrowVisibility = () => {
      Object.keys(containerRefs.current).forEach((id) => {
        if (containerRefs.current[id]) {
          setShowArrows((prev) => ({
            ...prev,
            [id]: containerRefs.current[id].scrollWidth > containerRefs.current[id].clientWidth,
          }));
        }
      });
    };
    updateArrowVisibility();
    window.addEventListener("resize", updateArrowVisibility);
    return () => window.removeEventListener("resize", updateArrowVisibility);
  }, [productsByShowroom]);

  const scrollLeft = (id) => {
    containerRefs.current[id]?.scrollBy({ left: -200, behavior: "smooth" });
  };

  const scrollRight = (id) => {
    containerRefs.current[id]?.scrollBy({ left: 200, behavior: "smooth" });
  };

  const handleImageLoad = (productId) => {
    setImageLoading((prev) => ({ ...prev, [productId]: false }));
  };

  const handleImageError = (productId) => {
    setImageLoading((prev) => ({ ...prev, [productId]: true }));
  };

  return (
    <div className="container mx-auto px-2 mt-4">
      {homePageShowrooms?.length > 0 ? (
        homePageShowrooms.map((showroom) => (
          <div key={showroom.showRoomID} className="mb-6">
            <div className="flex justify-between items-center mb-2 bg-green-600 p-1 rounded-md">
              <h2 className="text-sm flex items-center text-white">
                <span className="mr-2">🔥</span>
                {showroom.showRoomName}
              </h2>
              <button
                className="text-sm text-white hover:underline flex items-center"
                onClick={() => navigate(`/showroom/${showroom.showRoomID}`)}
              >
                View More <RightOutlined className="ml-1" />
              </button>
            </div>

            <div className="relative">
              {showArrows[showroom.showRoomID] && (
                <>
                  <button
                    className="hidden sm:block absolute top-1/2 transform -translate-y-1/2 left-2 z-10 bg-gray-300 shadow hover:bg-gray-400 transition-all duration-300 p-2 rounded-full"
                    onClick={() => scrollLeft(showroom.showRoomID)}
                  >
                    <LeftOutlined />
                  </button>
                  <button
                    className="hidden sm:block absolute top-1/2 transform -translate-y-1/2 right-2 z-10 bg-gray-300 shadow hover:bg-gray-400 transition-all duration-300 p-2 rounded-full"
                    onClick={() => scrollRight(showroom.showRoomID)}
                  >
                    <RightOutlined />
                  </button>
                </>
              )}
              <div
                ref={(el) => (containerRefs.current[showroom.showRoomID] = el)}
                className="flex gap-4 overflow-x-auto no-scrollbar scrollbar-hide px-2"
              >
                {loading ? (
                  [...Array(6)].map((_, index) => (
                    <div key={index} className="bg-white p-3 rounded-lg shadow-md min-w-[150px] md:min-w-[200px] lg:min-w-[250px] animate-pulse">
                      <div className="w-32 md:w-24 lg:w-48 h-32 bg-gray-300 rounded-lg"></div>
                      <div className="h-4 bg-gray-300 rounded w-3/4 mt-2"></div>
                      <div className="h-3 bg-gray-300 rounded w-1/2 mt-1"></div>
                    </div>
                  ))
                ) : productsByShowroom?.[showroom.showRoomID]?.length > 0 ? (
                  productsByShowroom[showroom.showRoomID].slice(0, 10).map((product) => (
                    <div
                    key={product.productID}
                    className="relative bg-white text-black p-3 rounded-lg shadow-md flex flex-col cursor-pointer transform transition-transform duration-200 hover:scale-105 min-w-[150px] md:min-w-[200px] lg:min-w-[250px]"
                    onClick={() => navigate(`/product/${product.productID}`)}
                  >
                    {product.oldPrice > 0 && (
                      <div className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded-full">
                        {`${Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100)}% OFF`}
                      </div>
                    )}
                    <div className="h-32 md:h-40 lg:h-48 flex items-center justify-center mb-3">
                      {imageLoading[product.productID] ? (
                        <div className="w-32 md:w-24 lg:w-48 h-32 bg-gray-300 rounded-lg animate-pulse"></div>
                      ) : (
                        <img
                          src={`https://smfteapi.salesmate.app/Media/Products_Images/${product.productImage.split("\\").pop()}`}
                          alt={product.productName}
                          className="w-32 md:w-24 lg:w-48 object-cover rounded-lg"
                          onLoad={() => handleImageLoad(product.productID)}
                          onError={() => handleImageError(product.productID)}
                        />
                      )}
                    </div>
                    <p className=" text-sm line-clamp-2">{product.productName}</p>

                    <p className="text-red-600 text-xs">
                      {new Intl.NumberFormat("en-GH", { style: "currency", currency: "GHS" }).format(product.price || 0)}
                    </p>
                    {product.oldPrice > 0 && (
                      <p className="text-gray-500 text-xs line-through">
                        {new Intl.NumberFormat("en-GH", { style: "currency", currency: "GHS" }).format(product.oldPrice)}
                      </p>
                    )}
                  </div>
                  
                  ))
                ) : (
                  <p className="text-white">No products found.</p>
                )}
              </div>
            </div>
          </div>
        ))
      ) : (
        <p className="text-white">No showrooms available.</p>
      )}

      <div className="flex justify-center mt-6 mb-6">
        <button
          onClick={() => navigate("/products")}
          className="bg-red-500 hover:bg-green-500 text-white py-1 px-4 rounded-full shadow-lg flex items-center gap-2 transition-all duration-300"
        >
          <ShoppingCartOutlined className="text-md" />
          Shop Now
        </button>
      </div>
    </div>
  );
};

export default ComboComponent;
